import React, { useState } from 'react';
import { CheckCircle, Clock, Shield, Home, Zap, ThermometerSun, Fan, ArrowRight, AlertTriangle, Share2, Mail, Link2, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import GlobalBanner from '@/components/GlobalBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InstantEstimatePage = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    home_sqft: '',
    system_type: '',
    system_age: '',
    service_needed: '',
    duct_condition: '',
    system_location: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [estimateResult, setEstimateResult] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStep1Submit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email || !formData.address) {
      toast.error('Please fill in all required fields');
      return;
    }
    setStep(2);
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    if (!formData.home_sqft || !formData.system_type || !formData.service_needed || !formData.duct_condition || !formData.system_location) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      // Submit lead to backend
      await axios.post(`${API}/leads`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        service_type: formData.service_needed,
        lead_tag: 'replacement_lead',
        city: formData.address,
        message: `Instant Estimate Request - Sq Ft: ${formData.home_sqft}, System: ${formData.system_type}, Age: ${formData.system_age}, Ductwork: ${formData.duct_condition}, Location: ${formData.system_location}`
      });

      // Calculate estimate based on inputs
      const sqft = parseInt(formData.home_sqft) || 1500;
      // Cap pricing at 5-ton residential max (~2500 sqft base)
      const pricingSqft = Math.min(sqft, 2500);
      const basePrice = pricingSqft * 8;
      
      let multiplier = 1;
      if (formData.system_type === 'heat-pump') multiplier = 1.2;
      if (formData.system_type === 'high-efficiency') multiplier = 1.4;
      if (formData.service_needed === 'ac-replacement') multiplier *= 1.0;
      if (formData.service_needed === 'full-system') multiplier *= 1.5;

      let lowEstimate = Math.round((basePrice * multiplier * 0.85) / 100) * 100;
      let highEstimate = Math.round((basePrice * multiplier * 1.15) / 100) * 100;

      // Ductwork condition adjustments
      if (formData.duct_condition === 'uneven_cooling') {
        lowEstimate += 1000;
        highEstimate += 3000;
      } else if (formData.duct_condition === 'duct_repairs') {
        lowEstimate += 2000;
        highEstimate += 6000;
      } else if (formData.duct_condition === 'not_sure') {
        lowEstimate += 2000;
        highEstimate += 2000;
      }

      // System location modifier (minor complexity adjustment)
      if (formData.system_location === 'attic') {
        lowEstimate += 300;
        highEstimate += 500;
      } else if (formData.system_location === 'crawlspace') {
        lowEstimate += 200;
        highEstimate += 400;
      } else if (formData.system_location === 'location_not_sure') {
        lowEstimate += 100;
        highEstimate += 200;
      }

      // Calculate tonnage based on Louisiana cooling load (~500 sq ft per ton)
      // Residential HVAC caps at 5 tons max
      const tonnage = Math.min(5, Math.max(1.5, Math.round((sqft / 500) * 2) / 2));

      // Calculate replacement risk based on system age
      let riskLevel = 'low';
      let riskLabel = 'Low Risk';
      let riskColor = '#22C55E';
      let riskText = 'Your system likely still has useful life remaining.';
      const ageVal = formData.system_age;
      if (ageVal === '15+') {
        riskLevel = 'very_high'; riskLabel = 'Very High Risk'; riskColor = '#EF4444';
        riskText = 'Your system is near the end of its typical lifespan.';
      } else if (ageVal === '10-15') {
        riskLevel = 'high'; riskLabel = 'High Risk'; riskColor = '#F97316';
        riskText = 'Replacement planning is recommended to avoid unexpected breakdowns.';
      } else if (ageVal === '5-10') {
        riskLevel = 'moderate'; riskLabel = 'Moderate Risk'; riskColor = '#EAB308';
        riskText = 'Your system may require major repairs within the next few years.';
      } else {
        riskLevel = 'low'; riskLabel = 'Low Risk'; riskColor = '#22C55E';
        riskText = 'Your system likely still has useful life remaining.';
      }

      const resultData = {
        low: lowEstimate,
        high: highEstimate,
        sqft: sqft,
        systemType: formData.system_type,
        tonnage: tonnage,
        ductCondition: formData.duct_condition,
        systemLocation: formData.system_location,
        systemAge: formData.system_age,
        riskLevel,
        riskLabel,
        riskColor,
        riskText,
        estimateId: null,
      };

      // Save estimate to backend for sharing
      try {
        const saveRes = await axios.post(`${API}/estimates`, {
          home_sqft: sqft,
          system_type: formData.system_type,
          system_age: formData.system_age,
          service_needed: formData.service_needed,
          duct_condition: formData.duct_condition,
          system_location: formData.system_location,
          low_estimate: lowEstimate,
          high_estimate: highEstimate,
          tonnage: tonnage,
          risk_level: riskLevel,
          risk_label: riskLabel,
        });
        resultData.estimateId = saveRes.data.estimate_id;
      } catch (err) {
        // Estimate saving failed — sharing link won't be available but estimate still shows
        if (process.env.NODE_ENV === 'development') {
          console.warn('Estimate save failed:', err);
        }
      }

      setEstimateResult(resultData);
      setStep(3);
      toast.success('Your estimate is ready!');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <GlobalBanner />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1
            className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Instant HVAC Estimate New Orleans
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Looking for an instant HVAC estimate in New Orleans? Our online tool provides real pricing 
            for AC replacement, HVAC installation, and system upgrades in minutes.
          </p>
          <p className="text-slate-500 mt-2">
            Skip the wait and get your online HVAC quote instantly.
          </p>
        </div>
      </section>

      {/* Estimate Tool */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 overflow-hidden">
            {/* Progress Steps */}
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-100">
              <div className="flex items-center justify-center gap-4">
                {[1, 2, 3].map((s) => (
                  <div key={s} className="flex items-center gap-2">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        step >= s
                          ? 'bg-[#0EA5E9] text-white'
                          : 'bg-slate-200 text-slate-500'
                      }`}
                    >
                      {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                    </div>
                    <span className={`text-sm hidden sm:block ${step >= s ? 'text-[#0F172A] font-medium' : 'text-slate-400'}`}>
                      {s === 1 ? 'Your Info' : s === 2 ? 'Home Details' : 'Your Estimate'}
                    </span>
                    {s < 3 && <div className="w-8 h-0.5 bg-slate-200 hidden sm:block" />}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 md:p-8">
              {/* Step 1: Contact Info */}
              {step === 1 && (
                <form onSubmit={handleStep1Submit} className="space-y-5">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Get Your Instant Estimate
                    </h2>
                    <p className="text-slate-500 mt-1">Enter your details to see real HVAC pricing</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-slate-700 font-medium">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="mt-1.5 h-12 border-slate-200 rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-slate-700 font-medium">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="mt-1.5 h-12 border-slate-200 rounded-lg"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-slate-700 font-medium">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="mt-1.5 h-12 border-slate-200 rounded-lg"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="address" className="text-slate-700 font-medium">Property Address *</Label>
                    <Input
                      id="address"
                      name="address"
                      type="text"
                      placeholder="123 Main St, New Orleans, LA"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="mt-1.5 h-12 border-slate-200 rounded-lg"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 rounded-full text-lg"
                  >
                    Continue to Home Details
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>

                  <p className="text-center text-sm text-slate-500">
                    🔒 Your information is secure and never shared
                  </p>
                </form>
              )}

              {/* Step 2: Home Details */}
              {step === 2 && (
                <form onSubmit={handleStep2Submit} className="space-y-5">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Tell Us About Your Home
                    </h2>
                    <p className="text-slate-500 mt-1">This helps us provide accurate pricing</p>
                  </div>

                  <div>
                    <Label htmlFor="home_sqft" className="text-slate-700 font-medium">Home Square Footage *</Label>
                    <Select value={formData.home_sqft} onValueChange={(v) => handleSelectChange('home_sqft', v)}>
                      <SelectTrigger className="mt-1.5 h-12 border-slate-200 rounded-lg">
                        <SelectValue placeholder="Select square footage" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1000">Under 1,000 sq ft</SelectItem>
                        <SelectItem value="1500">1,000 - 1,500 sq ft</SelectItem>
                        <SelectItem value="2000">1,500 - 2,000 sq ft</SelectItem>
                        <SelectItem value="2500">2,000 - 2,500 sq ft</SelectItem>
                        <SelectItem value="3000">2,500 - 3,000 sq ft</SelectItem>
                        <SelectItem value="3500">3,000+ sq ft</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="system_type" className="text-slate-700 font-medium">System Type *</Label>
                    <Select value={formData.system_type} onValueChange={(v) => handleSelectChange('system_type', v)}>
                      <SelectTrigger className="mt-1.5 h-12 border-slate-200 rounded-lg">
                        <SelectValue placeholder="Select system type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="central-ac">Central AC</SelectItem>
                        <SelectItem value="heat-pump">Heat Pump</SelectItem>
                        <SelectItem value="high-efficiency">High-Efficiency System</SelectItem>
                        <SelectItem value="ductless">Ductless Mini-Split</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="system_age" className="text-slate-700 font-medium">Current System Age</Label>
                    <Select value={formData.system_age} onValueChange={(v) => handleSelectChange('system_age', v)}>
                      <SelectTrigger className="mt-1.5 h-12 border-slate-200 rounded-lg">
                        <SelectValue placeholder="Select age (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0-5">0-5 years</SelectItem>
                        <SelectItem value="5-10">5-10 years</SelectItem>
                        <SelectItem value="10-15">10-15 years</SelectItem>
                        <SelectItem value="15+">15+ years</SelectItem>
                        <SelectItem value="unknown">Not sure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="service_needed" className="text-slate-700 font-medium">Service Needed *</Label>
                    <Select value={formData.service_needed} onValueChange={(v) => handleSelectChange('service_needed', v)}>
                      <SelectTrigger className="mt-1.5 h-12 border-slate-200 rounded-lg" data-testid="service-needed-select">
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ac-replacement">AC Replacement</SelectItem>
                        <SelectItem value="full-system">Full HVAC System</SelectItem>
                        <SelectItem value="heat-pump-upgrade">Heat Pump Upgrade</SelectItem>
                        <SelectItem value="repair-estimate">Repair Estimate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="duct_condition" className="text-slate-700 font-medium">What condition is your existing ductwork in? *</Label>
                    <Select value={formData.duct_condition} onValueChange={(v) => handleSelectChange('duct_condition', v)}>
                      <SelectTrigger className="mt-1.5 h-12 border-slate-200 rounded-lg" data-testid="duct-condition-select">
                        <SelectValue placeholder="Select ductwork condition" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="good_condition">My ductwork appears to be in good condition</SelectItem>
                        <SelectItem value="uneven_cooling">Some rooms do not cool evenly</SelectItem>
                        <SelectItem value="duct_repairs">My ductwork may need repairs</SelectItem>
                        <SelectItem value="not_sure">I am not sure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="system_location" className="text-slate-700 font-medium">What is the location of your HVAC system? *</Label>
                    <Select value={formData.system_location} onValueChange={(v) => handleSelectChange('system_location', v)}>
                      <SelectTrigger className="mt-1.5 h-12 border-slate-200 rounded-lg" data-testid="system-location-select">
                        <SelectValue placeholder="Select system location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="attic">Attic</SelectItem>
                        <SelectItem value="closet">Closet</SelectItem>
                        <SelectItem value="garage">Garage</SelectItem>
                        <SelectItem value="crawlspace">Crawlspace</SelectItem>
                        <SelectItem value="location_not_sure">Not sure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="flex-1 py-4 rounded-full"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 rounded-full text-lg"
                    >
                      {isSubmitting ? 'Calculating...' : 'Get My Estimate'}
                    </Button>
                  </div>
                </form>
              )}

              {/* Step 3: Results */}
              {step === 3 && estimateResult && (
                <div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    
                    <h2 className="text-2xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Your Instant HVAC Estimate
                    </h2>
                    <p className="text-slate-500 mb-6">Based on your home details and local pricing</p>

                    <div className="bg-gradient-to-br from-[#0F172A] to-slate-800 rounded-2xl p-6 text-white mb-6">
                      <p className="text-slate-300 text-sm mb-2">Estimated Price Range</p>
                      <p className="text-4xl font-bold mb-2" data-testid="estimate-price-range">
                        ${estimateResult.low.toLocaleString()} - ${estimateResult.high.toLocaleString()}
                      </p>
                      <p className="text-slate-400 text-sm">
                        For {estimateResult.sqft.toLocaleString()} sq ft home
                      </p>
                    </div>

                    <div className="bg-[#0EA5E9]/10 border border-[#0EA5E9]/20 rounded-xl p-4 mb-6 text-center">
                      <p className="text-sm text-[#0EA5E9] font-medium mb-1">Estimated System Size for Your Home</p>
                      <p className="text-2xl font-bold text-[#0F172A]" data-testid="estimate-tonnage" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {estimateResult.tonnage} Ton HVAC System
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Based on {estimateResult.sqft.toLocaleString()} sq ft and Louisiana cooling load</p>
                      {estimateResult.sqft > 2500 && (
                        <p className="text-xs text-[#F97316] mt-2 font-medium">Homes over 2,500 sq ft may require multiple systems or zoning for optimal comfort.</p>
                      )}
                    </div>
                  </div>

                  {/* Risk Score */}
                  <div className="rounded-xl border p-5 mb-6" style={{ borderColor: estimateResult.riskColor + '40' }} data-testid="risk-score-section">
                    <div className="flex items-center gap-3 mb-3">
                      <AlertTriangle className="w-5 h-5" style={{ color: estimateResult.riskColor }} />
                      <h3 className="font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        HVAC System Replacement Risk
                      </h3>
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <span
                        className="px-3 py-1 rounded-full text-sm font-bold text-white"
                        style={{ backgroundColor: estimateResult.riskColor }}
                        data-testid="risk-level-badge"
                      >
                        {estimateResult.riskLabel}
                      </span>
                    </div>
                    <p className="text-slate-600 text-sm" data-testid="risk-recommendation">
                      {estimateResult.riskText}
                    </p>
                  </div>

                  {/* Local Price Context */}
                  <div className="bg-slate-50 rounded-xl p-5 mb-6" data-testid="local-price-context">
                    <div className="flex items-center gap-2 mb-4">
                      <DollarSign className="w-5 h-5 text-[#0EA5E9]" />
                      <h3 className="font-bold text-[#0F172A] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Typical HVAC Replacement Costs in Southeast Louisiana
                      </h3>
                    </div>
                    <div className="space-y-2 mb-4">
                      {[
                        { ton: '2 Ton System', range: '$8,500 – $12,500 installed' },
                        { ton: '2.5 Ton System', range: '$9,500 – $14,000 installed' },
                        { ton: '3 Ton System', range: '$11,000 – $16,000 installed' },
                        { ton: '3.5 Ton System', range: '$12,500 – $17,500 installed' },
                        { ton: '4 Ton System', range: '$14,000 – $19,500 installed' },
                        { ton: '5 Ton System', range: '$16,500 – $22,500 installed' },
                      ].map((item) => (
                        <div key={item.ton} className="flex justify-between items-center py-1.5 border-b border-slate-200 last:border-0">
                          <span className="text-sm font-medium text-[#0F172A]">{item.ton}</span>
                          <span className="text-sm text-slate-500">{item.range}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 italic">
                      Actual pricing varies depending on ductwork condition, equipment efficiency, and installation complexity.
                    </p>
                  </div>

                  {/* What's Next */}
                  <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                    <p className="font-semibold text-[#0F172A] mb-2">What's Next?</p>
                    <p className="text-slate-600 text-sm">
                      A trusted local contractor will contact you within 24 hours to discuss your project 
                      and provide a detailed in-home estimate at no cost.
                    </p>
                  </div>

                  {/* Share Estimate */}
                  <div className="rounded-xl border border-slate-200 p-5 mb-6" data-testid="share-estimate-section">
                    <div className="flex items-center gap-2 mb-4">
                      <Share2 className="w-5 h-5 text-[#0EA5E9]" />
                      <h3 className="font-bold text-[#0F172A] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Share Your Estimate
                      </h3>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <Button
                        variant="outline"
                        className="rounded-lg text-xs font-semibold"
                        data-testid="share-estimate-btn"
                        onClick={() => {
                          if (estimateResult.estimateId && navigator.share) {
                            navigator.share({
                              title: 'My HVAC Estimate - NOLA HVAC Connect',
                              url: `${window.location.origin}/estimate-result/${estimateResult.estimateId}`,
                            }).catch(() => {});
                          } else if (estimateResult.estimateId) {
                            navigator.clipboard.writeText(`${window.location.origin}/estimate-result/${estimateResult.estimateId}`);
                            toast.success('Estimate link copied!');
                          } else {
                            toast.error('Estimate link not available');
                          }
                        }}
                      >
                        <Share2 className="w-3.5 h-3.5 mr-1" />
                        Share
                      </Button>
                      <Button
                        variant="outline"
                        className="rounded-lg text-xs font-semibold"
                        data-testid="email-estimate-btn"
                        onClick={() => {
                          const subject = encodeURIComponent('My HVAC Estimate from NOLA HVAC Connect');
                          const body = encodeURIComponent(
                            `Check out my HVAC estimate:\n\nHome: ${estimateResult.sqft.toLocaleString()} sq ft\nSystem Size: ${estimateResult.tonnage} Ton\nEstimate: $${estimateResult.low.toLocaleString()} - $${estimateResult.high.toLocaleString()}\nRisk: ${estimateResult.riskLabel}\n\n${estimateResult.estimateId ? `View details: ${window.location.origin}/estimate-result/${estimateResult.estimateId}` : ''}`
                          );
                          window.open(`mailto:?subject=${subject}&body=${body}`);
                        }}
                      >
                        <Mail className="w-3.5 h-3.5 mr-1" />
                        Email
                      </Button>
                      <Button
                        variant="outline"
                        className="rounded-lg text-xs font-semibold"
                        data-testid="copy-estimate-link-btn"
                        onClick={() => {
                          if (estimateResult.estimateId) {
                            navigator.clipboard.writeText(`${window.location.origin}/estimate-result/${estimateResult.estimateId}`);
                            toast.success('Estimate link copied to clipboard!');
                          } else {
                            toast.error('Estimate link not available');
                          }
                        }}
                      >
                        <Link2 className="w-3.5 h-3.5 mr-1" />
                        Copy Link
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Shield className="w-4 h-4 text-[#0EA5E9]" /> Licensed & Insured
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-[#0EA5E9]" /> Free In-Home Quote
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-[#0F172A] mb-12" style={{ fontFamily: 'Outfit, sans-serif' }}>
            How It Works
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Home, title: 'Enter Your Home Details', desc: 'Tell us about your home size and current system' },
              { icon: Zap, title: 'Select Your Preferences', desc: 'Choose the type of system and service you need' },
              { icon: ThermometerSun, title: 'Get Your Instant Quote', desc: 'See real pricing in minutes, no waiting' },
            ].map((item) => (
              <div key={item.title} className="text-center">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-[#0EA5E9]" />
                </div>
                <h3 className="font-bold text-[#0F172A] mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Can Estimate */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-[#0F172A] mb-12" style={{ fontFamily: 'Outfit, sans-serif' }}>
            What You Can Estimate
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: Fan, title: 'AC Replacement Cost', desc: 'Get pricing for new air conditioning units' },
              { icon: ThermometerSun, title: 'HVAC Installation Pricing', desc: 'Full system installation estimates' },
              { icon: Zap, title: 'Heat Pump Upgrades', desc: 'Energy-efficient heat pump pricing' },
              { icon: Shield, title: 'High-Efficiency Systems', desc: 'Premium system options and costs' },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-[#0EA5E9]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0F172A]">{item.title}</h3>
                  <p className="text-slate-500 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Paragraph */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-slate-600 leading-relaxed">
            Homeowners searching for an <a href="/instant-hvac-estimate-new-orleans" className="text-[#0EA5E9] hover:underline">instant HVAC estimate</a> can 
            now get real pricing online without waiting days for a contractor visit. Our platform provides 
            accurate <a href="/hvac-pricing-new-orleans" className="text-[#0EA5E9] hover:underline">HVAC pricing online</a>, allowing you to compare options and receive 
            an <a href="/instant-hvac-estimate-new-orleans" className="text-[#0EA5E9] hover:underline">instant AC quote</a> in minutes.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Get Your Instant HVAC Quote?
          </h2>
          <p className="text-slate-300 mb-8">
            No phone call required. Free estimate in minutes. Trusted local contractors.
          </p>
          <Button
            onClick={() => { setStep(1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg"
          >
            Get My Instant HVAC Quote
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InstantEstimatePage;
